/**
 * Öffentliche Partner-Bestätigungsseite (Jun-2026, User-Wunsch).
 * Aufgerufen über den Einladungslink ``/partner-bestaetigung/:token``. Der
 * Partner prüft/ergänzt seine Kontaktdaten und bestätigt die Zusammenarbeit.
 */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Handshake, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PublicLegalConsent from "../components/public/PublicLegalConsent";

export default function PartnerConfirmPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [legalOk, setLegalOk] = useState(false);
    const [form, setForm] = useState({ contact_name: "", phone: "", email: "", city: "" });

    useEffect(() => {
        api.get(`/service-providers/confirm/${token}`)
            .then((r) => {
                setData(r.data);
                setForm({ contact_name: r.data.contact_name || "", phone: r.data.contact_phone || "", email: r.data.contact_email || "", city: r.data.city || "" });
                if (r.data.status === "bestaetigt") setDone(true);
            })
            .catch(() => setError("Diese Einladung ist ungültig oder abgelaufen."))
            .finally(() => setLoading(false));
    }, [token]);

    const confirm = async () => {
        setBusy(true);
        try {
            await api.post(`/service-providers/confirm/${token}`, {
                contact_name: form.contact_name, contact_phone: form.phone,
                contact_email: form.email, city: form.city,
            });
            setDone(true);
            toast.success("Vielen Dank — Zusammenarbeit bestätigt!");
        } catch { toast.error("Bestätigung fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md bg-white border border-gray-200 rounded-sm shadow-sm p-6" data-testid="partner-confirm">
                {error ? (
                    <div className="text-center" data-testid="partner-confirm-error">
                        <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
                        <p className="text-gray-700">{error}</p>
                    </div>
                ) : done ? (
                    <div className="text-center" data-testid="partner-confirm-done">
                        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                        <h1 className="font-display font-black text-xl text-[#111827] mb-1">Bestätigt</h1>
                        <p className="text-sm text-gray-500">Vielen Dank! Die Zusammenarbeit mit <strong>{data?.dealer_name}</strong> ist bestätigt.</p>
                    </div>
                ) : (
                    <>
                        <Handshake className="w-10 h-10 text-indigo-600 mb-3" />
                        <h1 className="font-display font-black text-2xl text-[#111827] mb-1">Partner-Bestätigung</h1>
                        <p className="text-sm text-gray-500 mb-5"><strong>{data?.dealer_name}</strong> möchte <strong>{data?.name}</strong> als Partner hinterlegen. Bitte prüfe deine Kontaktdaten und bestätige.</p>
                        <div className="space-y-3">
                            <F label="Ansprechpartner"><Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="confirm-contact" /></F>
                            <F label="Telefon"><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="confirm-phone" /></F>
                            <F label="E-Mail"><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="confirm-email" /></F>
                            <F label="Ort"><Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="confirm-city" /></F>
                        </div>
                        <PublicLegalConsent token={token} portal="partner" onAcceptedChange={setLegalOk} accent="#4f46e5" className="mt-4" />
                        <Button onClick={confirm} disabled={busy || !legalOk} className="w-full mt-4 bg-[#111827] text-white" data-testid="partner-confirm-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Zusammenarbeit bestätigen</>}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

function F({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">{label}</label>
            {children}
        </div>
    );
}
